import { createContext, useContext, useState, ReactNode } from "react";
import { usePublishImpersonationTarget } from "@/contexts/ImpersonationTargetContext";

interface CobranzaImpersonationContextType {
  impersonatedEmail: string | null;
  impersonatedName: string | null;
  impersonatedPersonaId: number | null;
  impersonatedRoleId: number | null;
  /** `usuarios.auth_user_id` del impersonado: para lo que filtra por `auth.uid()` en BD
   *  (p. ej. Tickets del portal). Null si no se impersona o el usuario no tiene acceso. */
  impersonatedAuthUserId: string | null;
  /** Nombre del rol con el que entra al portal (para el perfil del header). */
  impersonatedRoleName: string | null;
  setImpersonated: (
    email: string,
    name: string,
    personaId: number | null,
    roleId: number | null,
    authUserId?: string | null,
    roleName?: string | null,
  ) => void;
  clearImpersonation: () => void;
  isImpersonating: boolean;
}

const CobranzaImpersonationContext = createContext<CobranzaImpersonationContextType>({
  impersonatedEmail: null,
  impersonatedName: null,
  impersonatedPersonaId: null,
  impersonatedRoleId: null,
  impersonatedAuthUserId: null,
  impersonatedRoleName: null,
  setImpersonated: () => {},
  clearImpersonation: () => {},
  isImpersonating: false,
});

export function CobranzaImpersonationProvider({ children }: { children: ReactNode }) {
  const [email, setEmail] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [personaId, setPersonaId] = useState<number | null>(null);
  const [roleId, setRoleId] = useState<number | null>(null);
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const [roleName, setRoleName] = useState<string | null>(null);

  const setImpersonated = (
    e: string, n: string, pid: number | null, rid: number | null, aid?: string | null, rn?: string | null,
  ) => {
    setEmail(e);
    setName(n);
    setPersonaId(pid);
    setRoleId(rid);
    setAuthUserId(aid ?? null);
    setRoleName(rn ?? null);
  };

  const clearImpersonation = () => {
    setEmail(null);
    setName(null);
    setPersonaId(null);
    setRoleId(null);
    setAuthUserId(null);
    setRoleName(null);
  };

  return (
    <CobranzaImpersonationContext.Provider
      value={{
        impersonatedEmail: email,
        impersonatedName: name,
        impersonatedPersonaId: personaId,
        impersonatedRoleId: roleId,
        impersonatedAuthUserId: authUserId,
        impersonatedRoleName: roleName,
        setImpersonated,
        clearImpersonation,
        isImpersonating: !!email,
      }}
    >
      {children}
    </CobranzaImpersonationContext.Provider>
  );
}

export const useCobranzaImpersonation = () => useContext(CobranzaImpersonationContext);

/**
 * Publica el impersonado de este contexto al núcleo global (menús/rol efectivos y
 * solo lectura de toda la app). Se llama SOLO desde el layout del portal: el
 * provider vive montado en toda la app y conserva la selección, pero al salir del
 * portal el layout se desmonta, se despublica y la app deja de estar en solo
 * lectura; al volver se republica.
 */
export function usePublicarVerComoCobranza(): void {
  const {
    impersonatedEmail: email, impersonatedPersonaId: personaId, impersonatedName: nombre,
    impersonatedRoleId: rolId, impersonatedRoleName: rolNombre, impersonatedAuthUserId: authUserId,
  } = useCobranzaImpersonation();
  // authUserId: las RPC de lectura del portal responden con el acceso de la persona.
  usePublishImpersonationTarget(
    email ? { email, personaId, nombre, rolId, rolNombre, authUserId } : null
  );
}
